import { useState } from "react";
import { saveOverlay, stepStatusLabel } from "../../js/api/filePacket";
import { STEP_STATUSES } from "../../js/api/filePacket/constants";
import { addClientDocument } from "../../js/db/documents";
import { useAuth } from "../../js/hooks/useAuth";

const emptyDraft = {
  title: "",
  description: "",
  status: "not_started",
  date: "",
  notes: "",
};

export default function FileCustomSteps({ file, onChange, compact = false }) {
  const { session } = useAuth();
  const [draft, setDraft] = useState(emptyDraft);
  const [error, setError] = useState("");
  const steps = file.customSteps || [];

  function persist(customSteps, metadata) {
    const lifecycle = { ...file.overlay.lifecycle };
    if (customSteps.length && lifecycle.custom_steps?.status === "not_started") {
      lifecycle.custom_steps = {
        status: "in_progress",
        notes: lifecycle.custom_steps.notes || "",
        updatedAt: new Date().toISOString(),
      };
    }
    saveOverlay(file.id, { customSteps, lifecycle }, session, "file.custom_step", metadata);
    onChange?.();
  }

  function addStep(event) {
    event.preventDefault();
    if (!draft.title.trim()) {
      setError("Give the custom step a title.");
      return;
    }
    setError("");
    const step = {
      id: crypto.randomUUID(),
      title: draft.title.trim(),
      description: draft.description.trim(),
      status: draft.status,
      date: draft.date,
      notes: draft.notes.trim(),
      attachments: [],
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    };
    persist([...steps, step], { title: step.title });
    setDraft(emptyDraft);
  }

  function updateStep(id, patch) {
    persist(
      steps.map((step) => (step.id === id ? { ...step, ...patch, updatedAt: new Date().toISOString() } : step)),
      { step_id: id }
    );
  }

  function removeStep(id) {
    persist(
      steps.filter((step) => step.id !== id),
      { removed: id }
    );
  }

  async function attachFile(step, fileInput) {
    const upload = fileInput.target.files?.[0];
    fileInput.target.value = "";
    if (!upload || !file.clientProfile?.id) return;
    try {
      const doc = await addClientDocument({
        ownerId: file.clientProfile.id,
        role: file.fileType,
        file: upload,
      });
      updateStep(step.id, {
        attachments: [
          ...(step.attachments || []),
          {
            id: doc.id,
            file_name: doc.file_name,
            mime_type: doc.mime_type,
            added_at: doc.created_at,
          },
        ],
      });
    } catch (err) {
      setError(err.message || "That attachment could not be saved.");
    }
  }

  if (compact) {
    return (
      <div className="file-rail-block">
        <h3>Custom steps</h3>
        {steps.length ? (
          <ul className="file-mini-list">
            {steps.map((step) => (
              <li key={step.id}>
                <strong>{step.title}</strong>
                <span className="muted">{stepStatusLabel(step.status)}</span>
              </li>
            ))}
          </ul>
        ) : (
          <p className="muted">Add office-only steps on the Timeline tab.</p>
        )}
      </div>
    );
  }

  return (
    <section className="card stack file-section">
      <div>
        <h2>Custom steps</h2>
        <p className="muted">
          Title search, special survey, unique escrow terms — office work that is not on the client
          checklist. These appear on the unified timeline and in the File Packet PDF.
        </p>
      </div>

      <form className="stack file-step-form" onSubmit={addStep}>
        <label className="field">
          <span className="field-label">Title</span>
          <input
            className="field-input"
            value={draft.title}
            onChange={(event) => setDraft((current) => ({ ...current, title: event.target.value }))}
            placeholder="Title search, survey, custom agreement…"
          />
        </label>
        <label className="field">
          <span className="field-label">Description</span>
          <textarea
            className="field-textarea"
            rows={3}
            value={draft.description}
            onChange={(event) => setDraft((current) => ({ ...current, description: event.target.value }))}
          />
        </label>
        <div className="two-col">
          <label className="field">
            <span className="field-label">Status</span>
            <select
              className="field-select"
              value={draft.status}
              onChange={(event) => setDraft((current) => ({ ...current, status: event.target.value }))}
            >
              {STEP_STATUSES.map((item) => (
                <option key={item.id} value={item.id}>
                  {item.label}
                </option>
              ))}
            </select>
          </label>
          <label className="field">
            <span className="field-label">Date</span>
            <input
              className="field-input"
              type="date"
              value={draft.date}
              onChange={(event) => setDraft((current) => ({ ...current, date: event.target.value }))}
            />
          </label>
        </div>
        <label className="field">
          <span className="field-label">Notes</span>
          <textarea
            className="field-textarea"
            rows={3}
            value={draft.notes}
            onChange={(event) => setDraft((current) => ({ ...current, notes: event.target.value }))}
          />
        </label>
        {error ? <p className="muted">{error}</p> : null}
        <button type="submit" className="primary-btn">
          Add custom step
        </button>
      </form>

      {steps.map((step) => (
        <article key={step.id} className="file-step-card">
          <header>
            <h3>{step.title}</h3>
            <button type="button" className="ghost-btn" onClick={() => removeStep(step.id)}>
              Remove
            </button>
          </header>
          {step.description ? <p>{step.description}</p> : null}
          <div className="two-col">
            <label className="field">
              <span className="field-label">Status</span>
              <select
                className="field-select"
                value={step.status}
                onChange={(event) => updateStep(step.id, { status: event.target.value })}
              >
                {STEP_STATUSES.map((item) => (
                  <option key={item.id} value={item.id}>
                    {item.label}
                  </option>
                ))}
              </select>
            </label>
            <label className="field">
              <span className="field-label">Date</span>
              <input
                className="field-input"
                type="date"
                value={step.date || ""}
                onChange={(event) => updateStep(step.id, { date: event.target.value })}
              />
            </label>
          </div>
          <label className="field">
            <span className="field-label">Notes</span>
            <textarea
              className="field-textarea"
              rows={3}
              value={step.notes || ""}
              onChange={(event) => updateStep(step.id, { notes: event.target.value })}
            />
          </label>
          <label className="field">
            <span className="field-label">Attachments</span>
            <input className="field-input" type="file" onChange={(event) => attachFile(step, event)} />
          </label>
          {step.attachments?.length ? (
            <ul className="file-mini-list">
              {step.attachments.map((item) => (
                <li key={item.id}>{item.file_name || item.name}</li>
              ))}
            </ul>
          ) : null}
        </article>
      ))}
    </section>
  );
}
